/**
 * ChatOnboarding — 5-stage conversational onboarding
 * Route: /onboarding/chat
 * Uses POST /api/onboarding/chat with stage routing, DB writes, auto grant cards at Stage 2.
 */
import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, CheckCircle2, ArrowRight, LayoutDashboard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";

const API_BASE = "/api";
const STAGE_LABELS = ["Org Identity", "Mission & Programs", "Budget & Capacity", "Past Grants", "Your Project"];
const STAGE_ICONS = ["🏛", "🎯", "💰", "📋", "🚀"];

const STAGE_STARTERS = [
  "Hi! I'm your ImpactPlexus setup guide. Let's get your organization set up in about 5 minutes.\n\nWhat's the name of your organization and which province are you based in? If you have your CRA charity number (BN), share that too — I'll auto-fill your details.",
  "Great! Now tell me about your mission. Who does your organization serve, and what are your main programs or services?",
  "Almost there — a couple of quick numbers. What's your approximate annual budget? And how many staff (and volunteers if you know)?",
  "Have you received any grants before? If so, name a funder or two and roughly how much. (It's okay if this is your first!)",
  "Last step! Describe the specific project you're looking to fund. What do you want to do, and how much funding are you seeking?",
];

function GrantCard({ card }) {
  const pct = Math.round((card.match_score || 0) * 100);
  return (
    <div className="bg-white border border-violet-200 rounded-xl p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-bold text-zinc-800 truncate">{card.funder_name}</p>
          <p className="text-[10.5px] text-zinc-500 truncate">{card.grant_name || card.funder_name}</p>
        </div>
        <div className="shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-violet-100 text-violet-700 font-black text-[14px]">
          {pct}
          <span className="text-[8px] font-normal ml-px">%</span>
        </div>
      </div>
      {card.grant_url && (
        <a href={card.grant_url} target="_blank" rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-[10.5px] text-violet-600 hover:underline">
          View grant <ArrowRight className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}

function Message({ msg }) {
  const isBot = msg.role === "assistant";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className={`flex gap-3 ${isBot ? "justify-start" : "justify-end"}`}
    >
      {isBot && (
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-teal-500 flex items-center justify-center shrink-0 text-white text-[12px] font-bold mt-1">
          IP
        </div>
      )}
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
        isBot
          ? "bg-white border border-zinc-200 text-zinc-800 rounded-tl-sm"
          : "bg-violet-600 text-white rounded-tr-sm"
      }`}>
        {msg.content}
        {msg.grant_cards?.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-[11px] font-bold text-violet-700 uppercase tracking-wide">Top Matches Found</p>
            {msg.grant_cards.map((c, i) => <GrantCard key={i} card={c} />)}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function StageProgress({ current, completeness }) {
  return (
    <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wide">Profile Setup</p>
        <p className="text-[11px] text-zinc-400">{Math.round(completeness * 100)}% complete</p>
      </div>
      <div className="flex gap-1.5 mb-2">
        {STAGE_LABELS.map((label, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className={`h-1.5 w-full rounded-full transition-all duration-500 ${
              i < current - 1 ? "bg-teal-500" : i === current - 1 ? "bg-violet-500" : "bg-zinc-200"
            }`} />
            <span className={`text-[8.5px] font-medium hidden sm:block ${
              i < current - 1 ? "text-teal-600" : i === current - 1 ? "text-violet-600" : "text-zinc-400"
            }`}>{STAGE_ICONS[i]}</span>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-zinc-600 font-medium">
        Stage {current}: {STAGE_LABELS[current - 1]}
      </p>
    </div>
  );
}

export default function ChatOnboarding() {
  const navigate = useNavigate();
  const [sessionId] = useState(() => `onb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [messages, setMessages] = useState([
    { role: "assistant", content: STAGE_STARTERS[0], grant_cards: [] }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(1);
  const [completeness, setCompleteness] = useState(0);
  const [entityBn, setEntityBn] = useState(null);
  const [canSkip, setCanSkip] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setLoading(true);

    const userMsg = { role: "user", content: text };
    setMessages(prev => [...prev, userMsg]);

    try {
      const res = await fetch(`${API_BASE}/onboarding/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          message: text,
          current_stage: stage,
          entity_bn: entityBn,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const botMsg = {
        role: "assistant",
        content: data.reply,
        grant_cards: data.grant_cards || [],
      };
      setMessages(prev => [...prev, botMsg]);

      // Update state from API response
      if (data.current_stage) setStage(data.current_stage);
      if (data.profile_completeness !== undefined) setCompleteness(data.profile_completeness);
      if (data.extracted_data?.bn_number) setEntityBn(data.extracted_data.bn_number);
      if (data.stage_complete && data.completeness >= 0.4) setCanSkip(true);

      // Auto-advance stage message if stage changed
      if (data.next_stage && data.next_stage !== stage && data.next_stage <= 5) {
        setTimeout(() => {
          setStage(data.next_stage);
          setMessages(prev => [...prev, {
            role: "assistant",
            content: STAGE_STARTERS[data.next_stage - 1],
            grant_cards: [],
          }]);
        }, 600);
      }

      // After stage 5 complete, show dashboard prompt
      if (data.current_stage === 5 && data.stage_complete) {
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: "assistant",
            content: "🎉 You're all set! Your profile is complete and FundMatch is running a full grant search for your project. Head to the dashboard to see your personalized results.",
            grant_cards: [],
          }]);
        }, 1000);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Sorry, I ran into an issue. Please try again in a moment.",
        grant_cards: [],
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden" style={{ height: "min(90vh, 720px)" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-teal-500 flex items-center justify-center">
              <span className="text-white font-black text-[13px]">IP</span>
            </div>
            <div>
              <p className="text-[14px] font-bold text-zinc-900">ImpactPlexus Setup</p>
              <p className="text-[11px] text-zinc-400">Conversational onboarding · ~5 minutes</p>
            </div>
          </div>
          {canSkip && (
            <Button
              variant="outline" size="sm"
              className="text-[12px] text-violet-600 border-violet-200 hover:bg-violet-50"
              onClick={() => navigate("/dashboard")}
            >
              <LayoutDashboard className="h-3.5 w-3.5 mr-1.5" /> Skip to Dashboard
            </Button>
          )}
        </div>

        {/* Stage Progress */}
        <StageProgress current={stage} completeness={completeness} />

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-zinc-50/50">
          <AnimatePresence initial={false}>
            {messages.map((msg, i) => <Message key={i} msg={msg} />)}
          </AnimatePresence>
          {loading && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3 justify-start">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-teal-500 flex items-center justify-center shrink-0 text-white text-[12px] font-bold">IP</div>
              <div className="bg-white border border-zinc-200 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 text-violet-500 animate-spin" />
                <span className="text-[12px] text-zinc-400">Thinking…</span>
              </div>
            </motion.div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Completeness ring + Input */}
        <div className="border-t border-zinc-100 px-4 py-3 bg-white">
          {completeness > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <CheckCircle2 key={i} className={`h-3 w-3 ${i < Math.round(completeness * 5) ? "text-teal-500" : "text-zinc-200"}`} />
                ))}
              </div>
              <span className="text-[10.5px] text-zinc-400">Stage {stage} of 5</span>
            </div>
          )}
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Type your answer… (Enter to send)"
              rows={2}
              className="flex-1 resize-none border border-zinc-200 rounded-xl px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300 placeholder:text-zinc-400"
            />
            <Button
              onClick={send}
              disabled={!input.trim() || loading}
              className="self-end bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-xl"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-zinc-400 mt-1.5 text-center">
            Your data is encrypted and never shared without your consent.
          </p>
        </div>
      </div>
    </div>
  );
}
